import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faEdit,
  faEye,
  faUpload,
  faSpinner,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../lib/supabaseClient';
import { useForm } from 'react-hook-form';
import PDFViewer from './PDFViewer';

function CompanyInfoModal({
  isOpen,
  onClose,
  company,
  onSave,
  onApproveDocument,
}) {
  const [editMode, setEditMode] = useState({});
  const [companyDrivers, setCompanyDrivers] = useState([]);
  const [isPDFOpen, setIsPDFOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentStatuses, setDocumentStatuses] = useState({
    transport_license: company?.transport_license,
    tax_plate: company?.tax_plate,
    cmr_insurance: company?.cmr_insurance,
    l2_document: company?.l2_document,
    tio_document: company?.tio_document,
    c2_document: company?.c2_document,
  });

  const [uploading, setUploading] = useState({
    transport_license: false,
    tax_plate: false,
    cmr_insurance: false,
    l2_document: false,
    tio_document: false,
    c2_document: false,
  });

  const [uploadProgress, setUploadProgress] = useState({
    transport_license: 0,
    tax_plate: 0,
    cmr_insurance: 0,
    l2_document: 0,
    tio_document: 0,
    c2_document: 0,
  });

  const [uploadError, setUploadError] = useState({
    transport_license: null,
    tax_plate: null,
    cmr_insurance: null,
    l2_document: null,
    tio_document: null,
    c2_document: null,
  });

  const fileInputRefs = {
    transport_license: useRef(null),
    tax_plate: useRef(null),
    cmr_insurance: useRef(null),
    l2_document: useRef(null),
    tio_document: useRef(null),
    c2_document: useRef(null),
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: {},
    watch,
  } = useForm({
    defaultValues: {
      company_name: company?.company_name || '',
      tax_number: company?.tax_number || '',
      phone_number: company?.phone_number || '',
      email: company?.email || '',
      address: company?.address || '',
    },
  });

  useEffect(() => {
    if (isOpen && company) {
      fetchCompanyDrivers();
      reset({
        company_name: company.company_name || '',
        tax_number: company.tax_number || '',
        phone_number: company.phone_number || '',
        email: company.email || '',
        address: company.address || '',
      });
    }
  }, [isOpen, company, reset]);

  useEffect(() => {
    if (company) {
      setDocumentStatuses({
        transport_license: company.transport_license,
        tax_plate: company.tax_plate,
        cmr_insurance: company.cmr_insurance,
        l2_document: company.l2_document,
        tio_document: company.tio_document,
        c2_document: company.c2_document,
      });
    }
  }, [company]);

  const fetchCompanyDrivers = async () => {
    if (!company) return;

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('company_id', company.id)
      .eq('user_type', 'company_driver');

    if (error) {
      console.error('Error fetching company drivers:', error);
    } else {
      setCompanyDrivers(data || []);
    }
  };

  const toggleEdit = (fieldName) => {
    setEditMode((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
    if (!editMode[fieldName]) {
      setValue(fieldName, company[fieldName]);
    }
  };

  const onSubmit = async (data) => {
    try {
      const updateData = {};
      Object.keys(editMode).forEach((field) => {
        if (editMode[field] && data[field] !== company[field]) {
          updateData[field] = data[field];
        }
      });

      if (Object.keys(updateData).length > 0) {
        const { error } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('id', company.id);

        if (error) throw error;
      }

      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      alert(error.message);
    }
  };

  const handleRemoveDriver = async (driverId) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          company_id: null,
          user_type: 'driver',
        })
        .eq('id', driverId);

      if (error) throw error;

      fetchCompanyDrivers();
    } catch (error) {
      console.error('Error removing driver:', error);
      alert('Sürücü çıkarılırken bir hata oluştu');
    }
  };

  const handleViewDocument = (documentUrl) => {
    if (!documentUrl) {
      alert('Belge bulunamadı');
      return;
    }

    // Check if the URL is for an image or PDF
    const isImage = /\.(jpg|jpeg|png|gif)$/i.test(documentUrl);

    if (isImage) {
      // Open image in a new tab
      window.open(documentUrl, '_blank');
    } else {
      // Open PDF in the PDF viewer
      setSelectedDocument(documentUrl);
      setIsPDFOpen(true);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDocumentStatus = async (docType, status, expiryDate = null) => {
    try {
      if (status === 'approved' && !expiryDate) {
        alert('Please select an expiry date before approving');
        return;
      }

      const documentUpdate = {
        ...company[docType],
        status: status,
        expiry_date: expiryDate,
        answer_date: new Date(),
      };

      setValue(`${docType}`, documentUpdate);

      setDocumentStatuses((prev) => ({
        ...prev,
        [docType]: documentUpdate,
      }));

      const { error } = await supabase
        .from('profiles')
        .update({
          [docType]: documentUpdate,
        })
        .eq('id', company.id);

      if (error) throw error;
      onSave();
    } catch (error) {
      setDocumentStatuses((prev) => ({
        ...prev,
        [docType]: company[docType],
      }));
      console.error('Error updating document status:', error);
      alert('Failed to update document status');
    }
  };

  const handleFileUpload = async (docType, file) => {
    if (!file || !company) return;

    try {
      // Reset states
      setUploadError((prev) => ({ ...prev, [docType]: null }));
      setUploading((prev) => ({ ...prev, [docType]: true }));
      setUploadProgress((prev) => ({ ...prev, [docType]: 0 }));

      // Validate file type (PDF or images)
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
      ];

      if (!allowedTypes.includes(file.type)) {
        throw new Error(
          'Sadece PDF veya resim dosyaları (JPG, PNG, GIF) yüklenebilir'
        );
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error("Dosya boyutu 10MB'dan küçük olmalıdır");
      }

      // Create a unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${docType}_${company.id}_${Date.now()}.${fileExt}`;
      const filePath = `documents/${company.id}/${fileName}`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('company-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
          onUploadProgress: (progress) => {
            const percent = Math.round(
              (progress.loaded / progress.total) * 100
            );
            setUploadProgress((prev) => ({ ...prev, [docType]: percent }));
          },
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('company-documents')
        .getPublicUrl(filePath);

      if (!publicUrlData || !publicUrlData.publicUrl) {
        throw new Error("Dosya URL'i alınamadı");
      }

      // Update document in database
      const documentUpdate = {
        url: publicUrlData.publicUrl,
        status: 'pending',
        created_at: new Date().toISOString(),
        file_name: file.name,
      };

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          [docType]: documentUpdate,
        })
        .eq('id', company.id);

      if (updateError) throw updateError;

      // Update local state
      setDocumentStatuses((prev) => ({
        ...prev,
        [docType]: documentUpdate,
      }));

      // Call onSave to refresh data
      onSave();
    } catch (error) {
      console.error(`Error uploading ${docType}:`, error);
      setUploadError((prev) => ({
        ...prev,
        [docType]: error.message || 'Dosya yüklenirken bir hata oluştu',
      }));
    } finally {
      setUploading((prev) => ({ ...prev, [docType]: false }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="inline-block w-full max-w-4xl my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg"
        >
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">
              Şirket Detayları
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div
            className="overflow-y-auto px-4 py-5"
            style={{ maxHeight: 'calc(100vh - 200px)' }}
          >
            <div className="px-6 flex flex-col">
              {/* Genel Bilgiler */}
              <div className="bg-gray-100 px-4 py-2 -mx-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Genel Bilgiler
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  {/* Şirket Adı */}
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">
                      Şirket Adı
                    </label>
                    <div className="flex items-center">
                      {editMode.company_name ? (
                        <input
                          {...register('company_name')}
                          className="text-sm w-full mr-2 px-2 py-1 border rounded"
                        />
                      ) : (
                        <p className="text-sm text-gray-900 flex-grow">
                          {company?.company_name}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleEdit('company_name')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <FontAwesomeIcon icon={faEdit} className="text-sm" />
                      </button>
                    </div>
                  </div>

                  {/* Vergi No */}
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">
                      Vergi No
                    </label>
                    <div className="flex items-center">
                      {editMode.tax_number ? (
                        <input
                          {...register('tax_number')}
                          className="text-sm w-full mr-2 px-2 py-1 border rounded"
                        />
                      ) : (
                        <p className="text-sm text-gray-900 flex-grow">
                          {company?.tax_number}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleEdit('tax_number')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <FontAwesomeIcon icon={faEdit} className="text-sm" />
                      </button>
                    </div>
                  </div>

                  {/* Telefon */}
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">
                      Telefon
                    </label>
                    <div className="flex items-center">
                      {editMode.phone_number ? (
                        <input
                          {...register('phone_number')}
                          className="text-sm w-full mr-2 px-2 py-1 border rounded"
                        />
                      ) : (
                        <p className="text-sm text-gray-900 flex-grow">
                          {company?.phone_number}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleEdit('phone_number')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <FontAwesomeIcon icon={faEdit} className="text-sm" />
                      </button>
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">
                      E-posta
                    </label>
                    <div className="flex items-center">
                      {editMode.email ? (
                        <input
                          {...register('email')}
                          className="text-sm w-full mr-2 px-2 py-1 border rounded"
                        />
                      ) : (
                        <p className="text-sm text-gray-900 flex-grow">
                          {company?.email}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleEdit('email')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <FontAwesomeIcon icon={faEdit} className="text-sm" />
                      </button>
                    </div>
                  </div>

                  {/* Adres */}
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-500 mb-1">
                      Adres
                    </label>
                    <div className="flex items-center">
                      {editMode.address ? (
                        <textarea
                          {...register('address')}
                          className="text-sm w-full mr-2 px-2 py-1 border rounded"
                          rows={3}
                        />
                      ) : (
                        <p className="text-sm text-gray-900 flex-grow">
                          {company?.address}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleEdit('address')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <FontAwesomeIcon icon={faEdit} className="text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Şirket Sürücüleri */}
              <div className="bg-gray-100 px-4 py-2 -mx-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Şirket Sürücüleri
                </h3>
              </div>
              <div className="p-6">
                <div className="bg-white rounded-lg border">
                  {companyDrivers.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Henüz sürücü atanmamış
                    </p>
                  ) : (
                    <div className="divide-y">
                      {companyDrivers.map((driver) => (
                        <div
                          key={driver.id}
                          className="flex justify-between items-center p-4"
                        >
                          <div>
                            <p className="font-medium">
                              {driver.first_name} {driver.last_name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {driver.phone_number}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveDriver(driver.id)}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Çıkar
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Belgeler */}
              <div className="bg-gray-100 px-4 py-2 -mx-6">
                <h3 className="text-lg font-medium text-gray-900">Belgeler</h3>
              </div>
              <div className="p-6 space-y-4">
                {/* Taşıma Belgesi */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h5 className="font-medium text-gray-900">
                        Taşıma Yetki Belgesi
                      </h5>
                      <p className="text-sm text-gray-500">
                        {documentStatuses?.transport_license?.created_at
                          ? `Yüklü: ${new Date(documentStatuses.transport_license.created_at).toLocaleDateString('tr-TR')}`
                          : 'Henüz yüklenmedi'}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusStyle(
                        documentStatuses?.transport_license?.status
                      )}`}
                    >
                      {documentStatuses?.transport_license?.status === 'pending'
                        ? 'Onay Bekliyor'
                        : documentStatuses?.transport_license?.status ===
                            'approved'
                          ? 'Onaylandı'
                          : documentStatuses?.transport_license?.status ===
                              'rejected'
                            ? 'Reddedildi'
                            : 'Yüklenmedi'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {documentStatuses?.transport_license?.url ? (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              handleViewDocument(
                                documentStatuses.transport_license.url
                              )
                            }
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            Belgeyi Görüntüle
                          </button>
                          <div className="flex items-center space-x-2">
                            <input
                              type="date"
                              {...register('transport_license.expiry_date')}
                              className="text-sm border rounded px-2 py-1"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                handleDocumentStatus(
                                  'transport_license',
                                  'approved',
                                  watch('transport_license.expiry_date')
                                )
                              }
                              className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                            >
                              Onayla
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleDocumentStatus(
                                  'transport_license',
                                  'rejected'
                                )
                              }
                              className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                            >
                              Reddet
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center">
                          <input
                            type="file"
                            id="transport_license_upload"
                            ref={fileInputRefs.transport_license}
                            className="hidden"
                            accept="application/pdf,image/jpeg,image/jpg,image/png,image/gif"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleFileUpload(
                                  'transport_license',
                                  e.target.files[0]
                                );
                              }
                            }}
                          />
                          <label
                            htmlFor="transport_license_upload"
                            className="cursor-pointer text-blue-600 hover:text-blue-800 text-sm flex items-center"
                          >
                            <FontAwesomeIcon icon={faUpload} className="mr-1" />
                            Belge Yükle
                          </label>
                        </div>
                      )}
                    </div>
                    {uploading.transport_license && (
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2 text-blue-500"
                        />
                        <span className="text-sm text-gray-600">
                          Yükleniyor... {uploadProgress.transport_license}%
                        </span>
                      </div>
                    )}
                    {uploadError.transport_license && (
                      <div className="text-sm text-red-500 flex items-center">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="mr-1"
                        />
                        {uploadError.transport_license}
                      </div>
                    )}
                  </div>
                </div>

                {/* Vergi Levhası */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h5 className="font-medium text-gray-900">
                        Vergi Levhası
                      </h5>
                      <p className="text-sm text-gray-500">
                        {documentStatuses?.tax_plate?.created_at
                          ? `Yüklü: ${new Date(documentStatuses.tax_plate.created_at).toLocaleDateString('tr-TR')}`
                          : 'Henüz yüklenmedi'}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusStyle(
                        documentStatuses?.tax_plate?.status
                      )}`}
                    >
                      {documentStatuses?.tax_plate?.status === 'pending'
                        ? 'Onay Bekliyor'
                        : documentStatuses?.tax_plate?.status === 'approved'
                          ? 'Onaylandı'
                          : documentStatuses?.tax_plate?.status === 'rejected'
                            ? 'Reddedildi'
                            : 'Yüklenmedi'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {documentStatuses?.tax_plate?.url ? (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              handleViewDocument(documentStatuses.tax_plate.url)
                            }
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            Belgeyi Görüntüle
                          </button>
                          <div className="flex items-center space-x-2">
                            <input
                              type="date"
                              {...register('tax_plate.expiry_date')}
                              className="text-sm border rounded px-2 py-1"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                handleDocumentStatus(
                                  'tax_plate',
                                  'approved',
                                  watch('tax_plate.expiry_date')
                                )
                              }
                              className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                            >
                              Onayla
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleDocumentStatus('tax_plate', 'rejected')
                              }
                              className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                            >
                              Reddet
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center">
                          <input
                            type="file"
                            id="tax_plate_upload"
                            ref={fileInputRefs.tax_plate}
                            className="hidden"
                            accept="application/pdf,image/jpeg,image/jpg,image/png,image/gif"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleFileUpload(
                                  'tax_plate',
                                  e.target.files[0]
                                );
                              }
                            }}
                          />
                          <label
                            htmlFor="tax_plate_upload"
                            className="cursor-pointer text-blue-600 hover:text-blue-800 text-sm flex items-center"
                          >
                            <FontAwesomeIcon icon={faUpload} className="mr-1" />
                            Belge Yükle
                          </label>
                        </div>
                      )}
                    </div>
                    {uploading.tax_plate && (
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2 text-blue-500"
                        />
                        <span className="text-sm text-gray-600">
                          Yükleniyor... {uploadProgress.tax_plate}%
                        </span>
                      </div>
                    )}
                    {uploadError.tax_plate && (
                      <div className="text-sm text-red-500 flex items-center">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="mr-1"
                        />
                        {uploadError.tax_plate}
                      </div>
                    )}
                  </div>
                </div>

                {/* CMR Sigortası */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h5 className="font-medium text-gray-900">
                        CMR Sigortası
                      </h5>
                      <p className="text-sm text-gray-500">
                        {documentStatuses?.cmr_insurance?.created_at
                          ? `Yüklü: ${new Date(documentStatuses.cmr_insurance.created_at).toLocaleDateString('tr-TR')}`
                          : 'Henüz yüklenmedi'}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusStyle(
                        documentStatuses?.cmr_insurance?.status
                      )}`}
                    >
                      {documentStatuses?.cmr_insurance?.status === 'pending'
                        ? 'Onay Bekliyor'
                        : documentStatuses?.cmr_insurance?.status === 'approved'
                          ? 'Onaylandı'
                          : documentStatuses?.cmr_insurance?.status ===
                              'rejected'
                            ? 'Reddedildi'
                            : 'Yüklenmedi'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {documentStatuses?.cmr_insurance?.url ? (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              handleViewDocument(
                                documentStatuses.cmr_insurance.url
                              )
                            }
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            Belgeyi Görüntüle
                          </button>
                          <div className="flex items-center space-x-2">
                            <input
                              type="date"
                              {...register('cmr_insurance.expiry_date')}
                              className="text-sm border rounded px-2 py-1"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                handleDocumentStatus(
                                  'cmr_insurance',
                                  'approved',
                                  watch('cmr_insurance.expiry_date')
                                )
                              }
                              className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                            >
                              Onayla
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleDocumentStatus(
                                  'cmr_insurance',
                                  'rejected'
                                )
                              }
                              className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                            >
                              Reddet
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center">
                          <input
                            type="file"
                            id="cmr_insurance_upload"
                            ref={fileInputRefs.cmr_insurance}
                            className="hidden"
                            accept="application/pdf,image/jpeg,image/jpg,image/png,image/gif"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleFileUpload(
                                  'cmr_insurance',
                                  e.target.files[0]
                                );
                              }
                            }}
                          />
                          <label
                            htmlFor="cmr_insurance_upload"
                            className="cursor-pointer text-blue-600 hover:text-blue-800 text-sm flex items-center"
                          >
                            <FontAwesomeIcon icon={faUpload} className="mr-1" />
                            Belge Yükle
                          </label>
                        </div>
                      )}
                    </div>
                    {uploading.cmr_insurance && (
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2 text-blue-500"
                        />
                        <span className="text-sm text-gray-600">
                          Yükleniyor... {uploadProgress.cmr_insurance}%
                        </span>
                      </div>
                    )}
                    {uploadError.cmr_insurance && (
                      <div className="text-sm text-red-500 flex items-center">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="mr-1"
                        />
                        {uploadError.cmr_insurance}
                      </div>
                    )}
                  </div>
                </div>

                {/* L2 Belgesi */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h5 className="font-medium text-gray-900">L2 Belgesi</h5>
                      <p className="text-sm text-gray-500">
                        {documentStatuses?.l2_document?.created_at
                          ? `Yüklü: ${new Date(documentStatuses.l2_document.created_at).toLocaleDateString('tr-TR')}`
                          : 'Henüz yüklenmedi'}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusStyle(
                        documentStatuses?.l2_document?.status
                      )}`}
                    >
                      {documentStatuses?.l2_document?.status === 'pending'
                        ? 'Onay Bekliyor'
                        : documentStatuses?.l2_document?.status === 'approved'
                          ? 'Onaylandı'
                          : documentStatuses?.l2_document?.status === 'rejected'
                            ? 'Reddedildi'
                            : 'Yüklenmedi'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {documentStatuses?.l2_document?.url ? (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              handleViewDocument(
                                documentStatuses.l2_document.url
                              )
                            }
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            Belgeyi Görüntüle
                          </button>
                          <button
                            type="button"
                            onClick={() => onApproveDocument('l2_document')}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                            disabled={
                              documentStatuses?.l2_document?.status ===
                              'approved'
                            }
                          >
                            İncele
                          </button>
                        </>
                      ) : (
                        <div className="flex items-center">
                          <input
                            type="file"
                            id="l2_document_upload"
                            ref={fileInputRefs.l2_document}
                            className="hidden"
                            accept="application/pdf,image/jpeg,image/jpg,image/png,image/gif"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleFileUpload(
                                  'l2_document',
                                  e.target.files[0]
                                );
                              }
                            }}
                          />
                          <label
                            htmlFor="l2_document_upload"
                            className="cursor-pointer text-blue-600 hover:text-blue-800 text-sm flex items-center"
                          >
                            <FontAwesomeIcon icon={faUpload} className="mr-1" />
                            Belge Yükle
                          </label>
                        </div>
                      )}
                    </div>
                    {uploading.l2_document && (
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2 text-blue-500"
                        />
                        <span className="text-sm text-gray-600">
                          Yükleniyor... {uploadProgress.l2_document}%
                        </span>
                      </div>
                    )}
                    {uploadError.l2_document && (
                      <div className="text-sm text-red-500 flex items-center">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="mr-1"
                        />
                        {uploadError.l2_document}
                      </div>
                    )}
                  </div>
                </div>

                {/* TIO Belgesi */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h5 className="font-medium text-gray-900">TIO Belgesi</h5>
                      <p className="text-sm text-gray-500">
                        {documentStatuses?.tio_document?.created_at
                          ? `Yüklü: ${new Date(documentStatuses.tio_document.created_at).toLocaleDateString('tr-TR')}`
                          : 'Henüz yüklenmedi'}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusStyle(
                        documentStatuses?.tio_document?.status
                      )}`}
                    >
                      {documentStatuses?.tio_document?.status === 'pending'
                        ? 'Onay Bekliyor'
                        : documentStatuses?.tio_document?.status === 'approved'
                          ? 'Onaylandı'
                          : documentStatuses?.tio_document?.status ===
                              'rejected'
                            ? 'Reddedildi'
                            : 'Yüklenmedi'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {documentStatuses?.tio_document?.url ? (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              handleViewDocument(
                                documentStatuses.tio_document.url
                              )
                            }
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            Belgeyi Görüntüle
                          </button>
                          <button
                            type="button"
                            onClick={() => onApproveDocument('tio_document')}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                            disabled={
                              documentStatuses?.tio_document?.status ===
                              'approved'
                            }
                          >
                            İncele
                          </button>
                        </>
                      ) : (
                        <div className="flex items-center">
                          <input
                            type="file"
                            id="tio_document_upload"
                            ref={fileInputRefs.tio_document}
                            className="hidden"
                            accept="application/pdf,image/jpeg,image/jpg,image/png,image/gif"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleFileUpload(
                                  'tio_document',
                                  e.target.files[0]
                                );
                              }
                            }}
                          />
                          <label
                            htmlFor="tio_document_upload"
                            className="cursor-pointer text-blue-600 hover:text-blue-800 text-sm flex items-center"
                          >
                            <FontAwesomeIcon icon={faUpload} className="mr-1" />
                            Belge Yükle
                          </label>
                        </div>
                      )}
                    </div>
                    {uploading.tio_document && (
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2 text-blue-500"
                        />
                        <span className="text-sm text-gray-600">
                          Yükleniyor... {uploadProgress.tio_document}%
                        </span>
                      </div>
                    )}
                    {uploadError.tio_document && (
                      <div className="text-sm text-red-500 flex items-center">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="mr-1"
                        />
                        {uploadError.tio_document}
                      </div>
                    )}
                  </div>
                </div>

                {/* C2 Belgesi */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h5 className="font-medium text-gray-900">C2 Belgesi</h5>
                      <p className="text-sm text-gray-500">
                        {documentStatuses?.c2_document?.created_at
                          ? `Yüklü: ${new Date(documentStatuses.c2_document.created_at).toLocaleDateString('tr-TR')}`
                          : 'Henüz yüklenmedi'}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusStyle(
                        documentStatuses?.c2_document?.status
                      )}`}
                    >
                      {documentStatuses?.c2_document?.status === 'pending'
                        ? 'Onay Bekliyor'
                        : documentStatuses?.c2_document?.status === 'approved'
                          ? 'Onaylandı'
                          : documentStatuses?.c2_document?.status === 'rejected'
                            ? 'Reddedildi'
                            : 'Yüklenmedi'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {documentStatuses?.c2_document?.url ? (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              handleViewDocument(
                                documentStatuses.c2_document.url
                              )
                            }
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            Belgeyi Görüntüle
                          </button>
                          <button
                            type="button"
                            onClick={() => onApproveDocument('c2_document')}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                            disabled={
                              documentStatuses?.c2_document?.status ===
                              'approved'
                            }
                          >
                            İncele
                          </button>
                        </>
                      ) : (
                        <div className="flex items-center">
                          <input
                            type="file"
                            id="c2_document_upload"
                            ref={fileInputRefs.c2_document}
                            className="hidden"
                            accept="application/pdf,image/jpeg,image/jpg,image/png,image/gif"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleFileUpload(
                                  'c2_document',
                                  e.target.files[0]
                                );
                              }
                            }}
                          />
                          <label
                            htmlFor="c2_document_upload"
                            className="cursor-pointer text-blue-600 hover:text-blue-800 text-sm flex items-center"
                          >
                            <FontAwesomeIcon icon={faUpload} className="mr-1" />
                            Belge Yükle
                          </label>
                        </div>
                      )}
                    </div>
                    {uploading.c2_document && (
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2 text-blue-500"
                        />
                        <span className="text-sm text-gray-600">
                          Yükleniyor... {uploadProgress.c2_document}%
                        </span>
                      </div>
                    )}
                    {uploadError.c2_document && (
                      <div className="text-sm text-red-500 flex items-center">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="mr-1"
                        />
                        {uploadError.c2_document}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="sticky bottom-0 bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="submit"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Değişiklikleri Kaydet
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Kapat
            </button>
          </div>
        </form>
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewer
        isOpen={isPDFOpen}
        onClose={() => setIsPDFOpen(false)}
        documentUrl={selectedDocument}
      />
    </div>
  );
}

export default CompanyInfoModal;
